import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function ForgotPasswordV2() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      
      // In development, show the token (remove in production)
      if (response.data.token) {
        setResetToken(response.data.token);
        toast.success('Kode generert! (Kun for utvikling)');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('En feil oppstod. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar Logo" 
                className="h-10"
              />
              <span className="text-gray-500">Glemt passord</span>
            </Link>
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
                alt="Valoria Solutions" 
                className="h-8"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center py-12">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Glemt passord?
                </h2>
                <p className="text-gray-600 mt-2">
                  Skriv inn e-postadressen din, så sender vi deg en kode for å tilbakestille passordet.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    E-postadresse
                  </label>
                  <input
                    type="email"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="din@epost.no"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium disabled:opacity-50"
                >
                  {loading ? 'Sender...' : 'Send tilbakestillingskode'}
                </button>

                <div className="text-center">
                  <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                    Tilbake til innlogging
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Sjekk e-posten din
              </h2>
              <p className="text-gray-600 mb-6">
                Hvis kontoen eksisterer, har vi sendt en 6-sifret kode til <strong>{email}</strong>
              </p>

              {resetToken && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800 mb-2">
                    <strong>Utviklingsmodus:</strong> Din kode er:
                  </p>
                  <p className="text-2xl font-bold text-yellow-900 tracking-widest">
                    {resetToken}
                  </p>
                </div>
              )}

              <Link
                to="/reset-password"
                className="inline-block w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 font-medium"
              >
                Gå til tilbakestilling
              </Link>

              <div className="mt-4">
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                  Tilbake til innlogging
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="absolute bottom-0 w-full bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
              alt="Valoria Solutions" 
              className="h-6"
            />
            <p className="text-center text-gray-500 text-sm">
              © 2025 Valoria Solutions AS. Systemklar - Internkontroll gjort enkelt.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ForgotPasswordV2;
